/**
 * visitedScreens — an in-memory, SESSION-SCOPED record of which console screens
 * the customer has opened during their current authenticated session.
 *
 * It exists for the one onboarding signal the server cannot supply (OMEGA-815):
 * the F12 checklist's "Check usage and billing" step is an ACTION the customer
 * performs, not a state the backend holds. There is no "has looked at billing"
 * column and there should not be one. Deriving the tick from usage data instead
 * would tick "Done" on the strength of a job having been billed, which claims
 * the customer did something they did not. So the screen that satisfies the step
 * records its own mount here, and the checklist reads it.
 *
 * Not `localStorage`, for the reason this module is careful about ownership at
 * all: a durable per-browser flag outlives the customer it was recorded for, so
 * the next customer to log in on that machine would find the step already
 * ticked. Onboarding completion is durable server-side (`users.onboarded_at`);
 * this is only a within-session nudge on the way there, and losing it on reload
 * understates what the customer did, which is the right direction to fail.
 *
 * THE LIFETIME IS EXPLICIT, and that is the part that bit. An earlier revision
 * kept a bare module-global `Set` whose only clear function had no application
 * caller, so the record outlived its customer anyway: A opened Billing, logged
 * out, and B logging in on the same tab found the step ticked. That is precisely
 * the `localStorage` failure above, reproduced inside a single tab, so "it is
 * only memory" is not what bounds this record. Two mechanisms now do, and both
 * are deliberate:
 *
 *   - `endSession()` clears it on logout. Session expiry routes through the same
 *     logout path (`useAuth`'s SESSION_EXPIRED_EVENT handler calls `logout`), so
 *     that single call covers both exits.
 *   - `beginSession(key)` adopts an owner and clears whenever the owner CHANGES.
 *     A missed `endSession` therefore cannot hand one customer's record to the
 *     next, and a record owned by nobody is neither readable nor writable. The
 *     belt is the clear; this is the braces.
 *
 * The key is whatever identifies the SIGNED-IN CUSTOMER, and the caller owns
 * that choice (`useAuth` derives it from the credential via
 * `engine/auth.js getSessionKey`). It deliberately is not the login-form
 * username: that is only populated by a login in the current page-load, so an
 * ordinary authenticated reload would arrive with no key, leave the record
 * unowned, and silently drop every visit made afterwards. Losing the VISITS on
 * a reload is intended; losing the OWNERSHIP is not, because an unowned record
 * is a write-only hole rather than a fresh start.
 *
 * A visit means the screen MOUNTED, not that its data loaded. A customer who
 * opens Billing while the API is degraded has still opened Billing, and the step
 * reads "check usage and billing", not "have billable usage".
 */

// Screen ids (the ConsoleShell SCREENS ids) opened in the current session.
const visited = new Set();
// Identity of the session the record belongs to; null when nobody is signed in.
let owner = null;
const listeners = new Set();

/**
 * Adopt `key` as the owner of the record, clearing it if the owner CHANGED.
 * Idempotent for the same key, so re-rendering does not wipe a live session's
 * record. A falsy key is ignored rather than treated as "no session": adopting
 * "" as an owner would make two different customers compare equal, so the
 * change-detection above would stop clearing between them. Callers must supply
 * a real key for a signed-in customer; `getSessionKey` is total for that reason.
 */
export function beginSession(key) {
  if (!key || owner === key) return;
  visited.clear();
  owner = key;
}

/** Clear the record and disown it. Called on logout, which expiry routes through. */
export function endSession() {
  visited.clear();
  owner = null;
}

/** Record that `screenId` mounted. Notifies subscribers only on a NEW visit. */
export function markVisited(screenId) {
  // The owner guard is the load-bearing half of the ownership invariant: a visit
  // recorded while nobody is signed in belongs to nobody, so it is dropped here
  // rather than banked against whoever logs in next.
  if (!owner || !screenId || visited.has(screenId)) return;
  visited.add(screenId);
  listeners.forEach((fn) => fn(screenId));
}

/**
 * Has `screenId` been opened in the CURRENT session?
 *
 * No owner check here, deliberately. The write side above is the only way an id
 * enters the record and it refuses to write without an owner, so the invariant
 * "every id in here belongs to the current owner" holds at the boundary rather
 * than being re-asserted on every read. Adding the check back would produce a
 * branch no test can reach, since every path that disowns also clears.
 */
export function hasVisited(screenId) {
  return visited.has(screenId);
}

/** Subscribe to visits. Returns an unsubscribe function. */
export function subscribeVisited(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
